import React, { useState } from 'react';
import { useParsedTransactions } from '../hooks/useParsedTransactions';

const BulkImport = ({ onAddTransactions, conversionRates, defaultExpanded = true }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isLoading, setIsLoading] = useState(false);
  const [imageFiles, setImageFiles] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  
  const {
    parsedTransactions,
    parseErrors,
    rawInput,
    setRawInput,
    parseInput,
    updateParsedTransaction,
    updateTransactionSplit,
    clearParsedTransactions,
    removeParsedTransaction,
    convertToGBP,
    EXPENSE_TYPES,
    MONEY_SOURCES
  } = useParsedTransactions();

  const handleImageDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    setImageFiles(prev => [...prev, ...files].slice(0, 10));
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files).filter(f => f.type.startsWith('image/'));
    setImageFiles(prev => [...prev, ...files].slice(0, 10));
    e.target.value = '';
  };

  const handleAnalyzeImages = async () => {
    if (imageFiles.length === 0) return;
    setIsAnalyzing(true);
    setAnalysisError('');
    try {
      const formData = new FormData();
      imageFiles.forEach(f => formData.append('screenshots', f));

      const token = localStorage.getItem('saver-auth-token');
      const response = await fetch('/api/analyze-bank-screenshots', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Error al analizar imágenes');

      setRawInput(data.text);
      setImageFiles([]);

      // Auto-parsear el resultado
      if (data.text.trim()) {
        parseInput(data.text, conversionRates);
      }
    } catch (error) {
      setAnalysisError(error.message);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleParse = async () => {
    if (!rawInput.trim()) {
      alert('Por favor, insira as transações para importar');
      return;
    }

    setIsLoading(true);
    try {
      const result = parseInput(rawInput, conversionRates);
      
      if (result.errors.length > 0) {
        console.log('Errores encontrados:', result.errors);
      }
      
      if (result.transactions.length === 0) {
        alert('Não foram encontradas transações válidas para importar');
      }
    } catch (error) {
      console.error('Erro ao analisar transações:', error);
      alert('Erro ao processar as transações');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddAllTransactions = () => {
    if (parsedTransactions.length === 0) {
      alert('Não há transações para adicionar');
      return;
    }

    // Convertir transacciones parseadas al formato esperado por el sistema
    const transactionsToAdd = [];
    
    parsedTransactions.forEach((transaction) => {
      if (transaction.hasSplit) {
        // Si tiene split, crear múltiples transacciones
        const activeSplits = transaction.splits.filter(split => split.amount && parseFloat(split.amount) > 0);
        
        activeSplits.forEach((split, index) => {
          const splitTransaction = {
            id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            merchantName: transaction.merchantName,
            currency: transaction.currency,
            originalAmount: parseFloat(split.amount),
            gbpAmount: convertToGBP(parseFloat(split.amount), transaction.currency, conversionRates),
            expenseType: split.expenseType,
            moneySource: split.moneySource,
            bankName: transaction.bankName, // Nuevo campo banco
            date: transaction.date,
            timestamp: Date.now() + index,
            isSplit: true,
            splitInfo: {
              totalSplits: activeSplits.length,
              splitIndex: index
            }
          };
          transactionsToAdd.push(splitTransaction);
        });
      } else {
        // Transacción simple
        const simpleTransaction = {
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          merchantName: transaction.merchantName,
          currency: transaction.currency,
          originalAmount: transaction.originalAmount,
          gbpAmount: convertToGBP(transaction.originalAmount, transaction.currency, conversionRates),
          expenseType: transaction.expenseType,
          moneySource: transaction.moneySource,
          bankName: transaction.bankName, // Nuevo campo banco
          date: transaction.date,
          timestamp: Date.now(),
          isSplit: false
        };
        transactionsToAdd.push(simpleTransaction);
      }
    });

    // Agregar todas las transacciones de una vez
    onAddTransactions(transactionsToAdd);

    // Limpiar después de agregar
    clearParsedTransactions();
    alert(`${transactionsToAdd.length} transações adicionadas com sucesso`);
  };

  const handleSplitToggle = (id, hasSplit) => {
    updateParsedTransaction(id, 'hasSplit', hasSplit);
    if (!hasSplit) {
      // Limpiar splits si se desactiva
      updateParsedTransaction(id, 'splits', [
        { expenseType: 'FOOD', moneySource: 'MARTIN BANK', amount: '' },
        { expenseType: 'FOOD', moneySource: 'MARTIN BANK', amount: '' },
        { expenseType: 'FOOD', moneySource: 'MARTIN BANK', amount: '' }
      ]);
    }
  };

  const calculateSplitTotal = (splits) => {
    return splits.reduce((total, split) => total + (parseFloat(split.amount) || 0), 0);
  };

  const isSplitValid = (transaction) => {
    if (!transaction.hasSplit) return true;
    const splitTotal = calculateSplitTotal(transaction.splits);
    return Math.abs(splitTotal - transaction.originalAmount) < 0.01;
  };

  const getRemainingAmount = (transaction) => {
    const splitTotal = calculateSplitTotal(transaction.splits);
    return transaction.originalAmount - splitTotal;
  };

  const fillRemainingAmount = (transactionId, splitIndex) => {
    const transaction = parsedTransactions.find(t => t.id === transactionId);
    if (!transaction) return;
    
    const remainingAmount = getRemainingAmount(transaction);
    if (remainingAmount > 0) {
      updateTransactionSplit(transactionId, splitIndex, 'amount', remainingAmount.toFixed(2));
    }
  };

  const setAllMoneySourceToAmanda = () => {
    const confirmed = window.confirm(
      `Tem certeza de que deseja alterar todas as fontes de dinheiro para "AMANDA BANK"?\n\n` +
      `Isso afetará ${parsedTransactions.length} transações e não pode ser desfeito.`
    );
    
    if (confirmed) {
      parsedTransactions.forEach(transaction => {
        if (!transaction.hasSplit) {
          updateParsedTransaction(transaction.id, 'moneySource', 'AMANDA BANK');
        } else {
          // Si tiene splits, cambiar todas las fuentes de los splits también
          transaction.splits.forEach((split, splitIndex) => {
            updateTransactionSplit(transaction.id, splitIndex, 'moneySource', 'AMANDA BANK');
          });
        }
      });
      
      alert(`Todas as fontes de dinheiro foram alteradas para "AMANDA BANK"`);
    }
  };

  const sampleText = `[02/07/2025] Mamba Studios | 44.64 GBP | Monzo
[26/06/2025] Amazon | 102.49 GBP | Revolut
[01/07/2025] Lidl Bcn-Can Batlln | 8.82 GBP | Revolut
[30/06/2025] McDonald's | 12.30 EUR | Revolut
[29/06/2025] Pão de Açúcar | 127.50 BRL | Nubank`;

  return (
    <div className="expense-card">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
            Importar do Agente GPT
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Importação em massa de transações
          </p>
        </div>
        <button 
          onClick={() => setIsExpanded(!isExpanded)}
          className="button-secondary text-xl"
        >
          {isExpanded ? '▼' : '▶'}
        </button>
      </div>
      
      {isExpanded && (
        <div className="mt-4 space-y-4">

          {/* Sección de upload de screenshots */}
          <div className="border border-dashed border-blue-300 rounded-lg p-4 bg-blue-50">
            <label className="block text-lg font-semibold text-gray-700 mb-3">
              Subir Screenshots de Bancos:
            </label>
            <div
              onDrop={handleImageDrop}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                isDragging ? 'border-blue-500 bg-blue-100' : 'border-blue-300 bg-white hover:bg-blue-50'
              }`}
              onClick={() => document.getElementById('screenshot-input').click()}
            >
              <p className="text-gray-500 text-sm">
                {isDragging ? 'Soltar aquí...' : 'Arrastrá imágenes aquí o hacé clic para seleccionar (máx. 10)'}
              </p>
              <input
                id="screenshot-input"
                type="file"
                multiple
                accept="image/*"
                className="hidden"
                onChange={handleImageSelect}
              />
            </div>

            {imageFiles.length > 0 && (
              <div className="mt-3">
                <div className="flex flex-wrap gap-2 mb-3">
                  {imageFiles.map((file, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={file.name}
                        className="w-16 h-16 object-cover rounded border border-gray-300"
                      />
                      <button
                        onClick={() => setImageFiles(prev => prev.filter((_, i) => i !== index))}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 text-xs flex items-center justify-center leading-none"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleAnalyzeImages}
                    disabled={isAnalyzing}
                    className="button-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isAnalyzing ? (
                      <>
                        <span>{imageFiles.length > 1 ? 'Analizando capturas...' : 'Analizando captura...'}</span>
                      </>
                    ) : (
                      <>
                        <span>{imageFiles.length > 1 ? 'Analizar capturas' : 'Analizar captura'}</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setImageFiles([])}
                    className="button-secondary text-sm"
                  >
                    Limpiar
                  </button>
                </div>
              </div>
            )}

            {analysisError && (
              <div className="mt-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
                {analysisError}
              </div>
            )}
          </div>

          {/* Botón para parsear - hidden, textarea removed */}
          <div className="flex space-x-4" style={{display:'none'}}>
            <button
              onClick={handleParse}
              disabled={isLoading || !rawInput.trim()}
              className="button-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <span>Processando...</span>
                </>
              ) : (
                <>
                  <span>Carregar Transações</span>
                </>
              )}
            </button>
            
            {(parsedTransactions.length > 0 || parseErrors.length > 0) && (
              <button
                onClick={clearParsedTransactions}
                className="button-secondary"
              >
                Limpar
              </button>
            )}
          </div>

          {/* Mostrar errores */}
          {parseErrors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="font-medium text-red-900 mb-2">Erros encontrados:</h3>
              <ul className="space-y-1">
                {parseErrors.map((error, index) => (
                  <li key={index} className="text-sm text-red-800">
                    <span className="font-mono bg-red-100 px-2 py-1 rounded text-xs">
                      {error.line}
                    </span>
                    <br />
                    <span className="text-red-600">{error.error}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Tabla de transacciones parseadas */}
          {parsedTransactions.length > 0 && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h3 className="font-medium text-gray-900">
                    Transações Analisadas ({parsedTransactions.length})
                  </h3>
                  <button
                    onClick={setAllMoneySourceToAmanda}
                    className="px-3 py-1 text-xs bg-pink-100 text-pink-700 rounded hover:bg-pink-200 transition-colors"
                    title="Alterar todas as fontes de dinheiro para Amanda Bank"
                  >
                    Todas → Amanda Bank
                  </button>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left py-2 px-3">Data</th>
                      <th className="text-left py-2 px-3">Estabelecimento</th>
                      <th className="text-right py-2 px-3">Valor</th>
                      <th className="text-left py-2 px-3">Tipo</th>
                      <th className="text-left py-2 px-3">Fonte</th>
                      <th className="text-center py-2 px-3">Split</th>
                      <th className="text-center py-2 px-3">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedTransactions.map((transaction) => (
                      <React.Fragment key={transaction.id}>
                        <tr className="border-b border-gray-100">
                          <td className="py-2 px-3 text-gray-600">
                            {new Date(transaction.date).toLocaleDateString('es-ES')}
                          </td>
                          <td className="py-2 px-3 font-medium">
                            {transaction.merchantName}
                          </td>
                          <td className="py-2 px-3 text-right">
                            <div>{transaction.originalAmount.toFixed(2)} {transaction.currency}</div>
                            <div className="text-xs text-gray-500">
                              ≈ £{transaction.gbpAmount.toFixed(2)}
                            </div>
                          </td>
                          <td className="py-2 px-3">
                            {!transaction.hasSplit ? (
                              <select
                                value={transaction.expenseType}
                                onChange={(e) => updateParsedTransaction(transaction.id, 'expenseType', e.target.value)}
                                className="text-xs border border-gray-300 rounded px-2 py-1"
                              >
                                {EXPENSE_TYPES.map(type => (
                                  <option key={type} value={type}>{type}</option>
                                ))}
                              </select>
                            ) : (
                              <span className="text-xs text-gray-500">Split activo</span>
                            )}
                          </td>
                          <td className="py-2 px-3">
                            {!transaction.hasSplit ? (
                              <select
                                value={transaction.moneySource}
                                onChange={(e) => updateParsedTransaction(transaction.id, 'moneySource', e.target.value)}
                                className="text-xs border border-gray-300 rounded px-2 py-1"
                              >
                                {MONEY_SOURCES.map(source => (
                                  <option key={source} value={source}>{source}</option>
                                ))}
                              </select>
                            ) : (
                              <span className="text-xs text-gray-500">Ver split</span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-center">
                            <input
                              type="checkbox"
                              checked={transaction.hasSplit}
                              onChange={(e) => handleSplitToggle(transaction.id, e.target.checked)}
                              className="w-4 h-4"
                            />
                          </td>
                          <td className="py-2 px-3 text-center">
                            <button
                              onClick={() => removeParsedTransaction(transaction.id)}
                              className="text-red-600 hover:text-red-800 text-xs"
                            >
                              X
                            </button>
                          </td>
                        </tr>
                        
                        {/* Mostrar configuración de split si está activo */}
                        {transaction.hasSplit && (
                          <tr className="bg-gray-50">
                            <td colSpan="7" className="p-3">
                              <div className="space-y-2">
                                <h4 className="text-sm font-medium text-gray-700">Configurar Split:</h4>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                  {transaction.splits.map((split, splitIndex) => (
                                    <div key={splitIndex} className="border border-gray-200 rounded p-2 bg-white">
                                      <div className="text-xs text-gray-600 mb-1">Split {splitIndex + 1}</div>
                                      <div className="space-y-1">
                                        <select
                                          value={split.expenseType}
                                          onChange={(e) => updateTransactionSplit(transaction.id, splitIndex, 'expenseType', e.target.value)}
                                          className="w-full text-xs border border-gray-300 rounded px-2 py-1"
                                        >
                                          {EXPENSE_TYPES.map(type => (
                                            <option key={type} value={type}>{type}</option>
                                          ))}
                                        </select>
                                        <select
                                          value={split.moneySource}
                                          onChange={(e) => updateTransactionSplit(transaction.id, splitIndex, 'moneySource', e.target.value)}
                                          className="w-full text-xs border border-gray-300 rounded px-2 py-1"
                                        >
                                          {MONEY_SOURCES.map(source => (
                                            <option key={source} value={source}>{source}</option>
                                          ))}
                                        </select>
                                        <div className="flex items-center space-x-1">
                                          <input
                                            type="number"
                                            step="0.01"
                                            value={split.amount}
                                            onChange={(e) => updateTransactionSplit(transaction.id, splitIndex, 'amount', e.target.value)}
                                            className="flex-1 text-xs border border-gray-300 rounded px-2 py-1"
                                            placeholder="0.00"
                                          />
                                          <button
                                            type="button"
                                            onClick={() => fillRemainingAmount(transaction.id, splitIndex)}
                                            className="px-1 py-1 text-xs bg-blue-100 text-blue-600 rounded hover:bg-blue-200 transition-colors"
                                            title={`Completar con el monto restante: ${getRemainingAmount(transaction).toFixed(2)}`}
                                          >
                                            🔄
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                <div className="text-xs">
                                  <span className={`${isSplitValid(transaction) ? 'text-green-600' : 'text-red-600'}`}>
                                    Total split: {calculateSplitTotal(transaction.splits).toFixed(2)} / {transaction.originalAmount.toFixed(2)}
                                  </span>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Botón para agregar todas las transacciones */}
          {parsedTransactions.length > 0 && (
            <div className="flex justify-end">
              <button
                onClick={handleAddAllTransactions}
                className="button-primary flex items-center space-x-2"
                disabled={parsedTransactions.some(t => t.hasSplit && !isSplitValid(t))}
              >
                <span>Adicionar Todas as Transações ({parsedTransactions.length})</span>
              </button>
            </div>
          )}

        </div>
      )}
    </div>
  );
};

export default BulkImport; 
